import React, { useContext, useRef } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import SvgHeadingIconBus from '@app/icons/heading/HeadingIconBus.tsx';
import SvgHeadingIconVideo from '@app/icons/heading/HeadingIconVideo.tsx';
import TabularDeleteButton from '@components/ringil3/Buttons/TabularDeleteButton.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgEdit from '@components/ringil3/icons/buttons/Edit.tsx';
import { useCreateInputPasteTabular } from '@components/framework/input/InputPasteTabular/InputPasteTabular.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  AllowedCarPlateResponse,
  ImportCameraInput,
  ListAllowedCarPlatesFilter,
  ListCompanyCameraResponse,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import CamsTabs from '@app/pages/cams/tabs/CamsTabs.tsx';
import { useCreateAddCarPlateSidebar } from '@app/pages/cams/seidebars/AddCarPlateSidebar.tsx';
import AddButton from '@components/framework/buttons/AddButton.tsx';
import { useCreateCameraSidebar } from '@app/pages/cams/seidebars/AddCameraSidebar.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';

interface ComponentProps {}

export interface CarPlates {
  carPlate: string;
  firstName?: string;
  secondName?: string;
  cardId?: string;
  validSince: string;
  validTill: string;
}

type TableRef = { reloadData: () => void; selectedObjects: unknown[]; clear: () => void };

const CamsDetail: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const {
    listCompanyCameras,
    importCameras,
    deleteCam,
    listAllowedCarPlates,
    deleteAllowedCamPlate,
    importAllowedCarPlates,
  } = useContext(ApiContext);

  const { loggedUser } = useContext(AuthenticatedUserContext);

  // Section-level access encoded as reusable, registered security rules (surfaced in the security overlay).
  const internalPlatesFlag = useSecuredFlag('cam-internal-plates', {
    kind: 'visibility',
    description: 'Internal allowed car plates (SPZ) settings',
    permissions: [PermissionTypesEnum.PERMISSION_CAM_SETTINGS],
  });
  const registeredCamsFlag = useSecuredFlag('cam-registered-cams', {
    kind: 'visibility',
    description: 'Registered cameras settings',
    permissions: [PermissionTypesEnum.PERMISSION_CAM_SETTINGS_REGISTERED_CAMS],
  });

  const timezoneToUse = loggedUser?.defaultTimezoneId ?? 'Europe/Prague';

  const plateTableRef = useRef<TableRef>(null);
  const cameraTableRef = useRef<TableRef>(null);

  const getValidFromTo = (record: AllowedCarPlateResponse) => {
    const hasNewFields = isNotNullOrUndefined(record.utcStart) && isNotNullOrUndefined(record.timezoneId);
    const validFromUtc = hasNewFields ? record.utcStart : record.validSince;
    const validToUtc = hasNewFields ? record.utcEnd : record.validTill;
    const tz = hasNewFields ? record.timezoneId : 'Europe/Prague';
    return { validFromUtc, validToUtc, tz };
  };

  const {
    InputPasteTabular,
    PasteTabularButton,
  } = useCreateInputPasteTabular<CarPlates>(['carPlate', 'firstName', 'secondName', 'cardId', 'validSince', 'validTill']);
  const {
    InputPasteTabular: InputPasteTabularCams,
    PasteTabularButton: PasteTabularButtonCams,
  } = useCreateInputPasteTabular<ImportCameraInput>(['externalId', 'name', 'cameraType', 'companyLocationId']);

  const { setVisible: setVisibleAddCarPlate, AddCarPlateSidebar, setCarPlateId, carPlateId } = useCreateAddCarPlateSidebar();
  const { setVisible: setVisibleCam, AddCameraSidebar } = useCreateCameraSidebar();

  const loadCarPlatesFromApi = (filter: ListAllowedCarPlatesFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<AllowedCarPlateResponse>) => void) => {
    listAllowedCarPlates(filter, pageable, d => onSuccess(d));
  };

  // Cameras are not paginated on the backend; adapt the single-shot list into the table's PagedData shape.
  const loadCamerasFromApi = (_filter: unknown, _pageable: Pageable, onSuccess: (pagedData: PagedData<ListCompanyCameraResponse>) => void) => {
    listCompanyCameras(d => {
      const cameras = d?.cameras ?? [];
      onSuccess({ data: cameras, total: cameras.length, page: 1, size: cameras.length || 1 });
    });
  };

  const deleteAllowedCamHandler = (id: string) => {
    deleteAllowedCamPlate(id, () => {
      plateTableRef.current?.reloadData();
    });
  };

  const carPlateFiltersConf: FilterConfig[] = [
    { filterType: FilterType.InputText, placeholder: tr('CamsSettings.spz', 'SPZ'), value: ['carPlate'] },
    { filterType: FilterType.InputText, placeholder: tr('CamsSettings.firstName', 'First name'), value: ['firstName'] },
    { filterType: FilterType.InputText, placeholder: tr('CamsSettings.secondName', 'Second name'), value: ['secondName'] },
    { filterType: FilterType.InputText, placeholder: tr('CamsSettings.cardId', 'Card ID'), value: ['cardId'] },
  ];

  const carPlateColumns: ColumnConfig<AllowedCarPlateResponse>[] = [
    { id: 'carPlate', header: 'CamsSettings.spz', headerDefault: 'SPZ', order: 1, render: r => <>{r.carPlate}</> },
    { id: 'firstName', header: 'CamsSettings.firstName', headerDefault: 'First name', order: 2, render: r => <>{r.firstName}</> },
    { id: 'secondName', header: 'CamsSettings.secondName', headerDefault: 'Second name', order: 3, render: r => <>{r.secondName}</> },
    { id: 'cardId', header: 'CamsSettings.cardId', headerDefault: 'Card ID', order: 4, render: r => <>{r.cardId}</> },
    { id: 'matchType', header: 'CamsSettings.matchType', headerDefault: 'Typ shody', order: 5, render: r => <>{tr(`CarPlateMatchType.${r.allowedCarPlateMatchType}`, r.allowedCarPlateMatchType)}</> },
    {
      id: 'validFrom',
      order: 6,
      headerRender: () => (
        <div className={'flex flex-col gap-1'}>
          <span>{tr('CamsSettings.platnostOd', 'Platnost od')}</span>
          <COTimezone timezoneId={timezoneToUse} />
        </div>
      ),
      render: r => {
        const { validFromUtc, tz } = getValidFromTo(r);
        return <>{stringDateTimeWithLocaleFromApiStringInTz(validFromUtc, tz)}</>;
      },
    },
    {
      id: 'validTo',
      order: 7,
      headerRender: () => (
        <div className={'flex flex-col gap-1'}>
          <span>{tr('CamsSettings.platnostDo', 'Platnost do')}</span>
          <COTimezone timezoneId={timezoneToUse} />
        </div>
      ),
      render: r => {
        const { validToUtc, tz } = getValidFromTo(r);
        return <>{stringDateTimeWithLocaleFromApiStringInTz(validToUtc, tz)}</>;
      },
    },
    {
      id: 'actions',
      order: 8,
      width: '150px',
      headerRender: () => null,
      render: r => (
        <div className={'flex flex-row gap-2'}>
          <RingilButton
            mode={'table'}
            icon={<SvgEdit />}
            onClick={() => {
              setCarPlateId(r.id);
              setVisibleAddCarPlate(true);
            }}
          />
          <TabularDeleteButton
            onClick={() => {
              deleteAllowedCamHandler(r.id);
            }}
            showConfirm
          />
        </div>
      ),
    },
  ];

  const cameraColumns: ColumnConfig<ListCompanyCameraResponse>[] = [
    { id: 'externalId', header: 'CamsSettings.idKamery', headerDefault: 'ID kamery', order: 1, render: r => <>{r.externalId}</> },
    { id: 'name', header: 'CamsSettings.pojmenovani', headerDefault: 'Pojmenování', order: 2, render: r => <>{r.name}</> },
    { id: 'cameraType', header: 'CamsSettings.typ', headerDefault: 'Typ', order: 3, render: r => <>{r.cameraType}</> },
    { id: 'locationName', header: 'CamsSettings.lokace', headerDefault: 'Lokace', order: 4, render: r => <>{r.locationName}</> },
    {
      id: 'actions',
      order: 5,
      width: '100px',
      headerRender: () => null,
      render: r => (
        <TabularDeleteButton
          onClick={() => {
            deleteCam(r.id, () => {
              cameraTableRef.current?.reloadData();
            });
          }}
        />
      ),
    },
  ];

  return (
    <div className={'flex flex-col gap-2'}>
      <BreadCrumbComponent
        items={[
          { label: tr(`ReservationDetail.bcReservationList`, `Seznam rezervací`), url: nav.createNavigationLink(nav.urlFunctions.createReservationList()) },
          { label: tr('CamsDetail.kamery', 'Kamery') },
        ]}
      />
      <COHeadline variant={'h2'} title={tr('CamsSettings.kamery', 'Kamery')} />
      <CamsTabs selectedPanel={'setting'} />

      <div className={'flex flex-col gap-2'}>
        <SecuredArea id="cam-internal-plates-section" visible={internalPlatesFlag} label="Internal SPZ" description="The company's internal car plates for the entry cameras">
          <div className={'flex flex-col gap-2'}>
            <div className={'my-3 flex flex-row items-center justify-between'}>
              <COHeadline variant={'h2'} title={tr('CamsSettings.interniSpz', 'Interní SPZ')} icon={<SvgHeadingIconBus />}></COHeadline>
              <PasteTabularButton />
            </div>

            <RingilDataTable
              ref={plateTableRef}
              columnConfig={carPlateColumns as ColumnConfig<unknown>[]}
              filtersConf={carPlateFiltersConf}
              loadDataFromApi={loadCarPlatesFromApi}
              filterName={'cams_settings'}
            />

            <AddButton
              onClick={() => {
                setCarPlateId(undefined);
                setVisibleAddCarPlate(true);
              }}
              title={tr('CamsSettings.pridatSpz', 'Přidat SPZ')}
              fullWidth
            />
          </div>
        </SecuredArea>

        <SecuredArea id="cam-registered-cams-section" visible={registeredCamsFlag} label="Registered cameras" description="Entry cameras registered for the company">
          <div className={'flex flex-col gap-2'}>
            <div className={'my-3 flex flex-row items-center justify-between'}>
              <COHeadline variant={'h2'} title={tr('CamsSettings.evidovaneKamery', 'Evidované kamery')} icon={<SvgHeadingIconVideo />}></COHeadline>
              <PasteTabularButtonCams />
            </div>

            <RingilDataTable
              ref={cameraTableRef}
              columnConfig={cameraColumns as ColumnConfig<unknown>[]}
              loadDataFromApi={loadCamerasFromApi}
              filterName={'cams_settings_cameras'}
              hidePaginator
              enableFilterV2={false}
            />

            <AddButton onClick={() => setVisibleCam(true)} title={tr('CamsSettings.pridatKameru', 'Přidat kameru')} fullWidth />
          </div>
        </SecuredArea>
      </div>

      <InputPasteTabular
        onSave={d => {
          importAllowedCarPlates(
            {
              allowedCarPlates: d,
            },
            () => {
              plateTableRef.current?.reloadData();
            },
          );
        }}
      />

      <InputPasteTabularCams
        onSave={d => {
          importCameras(
            {
              cameras: d,
            },
            () => {
              cameraTableRef.current?.reloadData();
            },
          );
        }}
      />

      <AddCarPlateSidebar
        carPlateId={carPlateId}
        onSaved={() => {
          setVisibleAddCarPlate(false);
          setCarPlateId(undefined);
          plateTableRef.current?.reloadData();
        }}
      ></AddCarPlateSidebar>

      <AddCameraSidebar
        onSaved={() => {
          setVisibleCam(false);
          cameraTableRef.current?.reloadData();
        }}
      ></AddCameraSidebar>
    </div>
  );
};

export default CamsDetail;